import React from 'react';
import { Link } from 'react-router-dom';
import { Compass, ShieldCheck, Stethoscope, Brain, ArrowRight } from 'lucide-react';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import WhatsAppCta from '../components/WhatsAppCta';
import { useReveal } from '../hooks/useInView';
import { BRAND_NAME } from '../utils/brand';

const VALUES = [
  {
    icon: ShieldCheck,
    title: 'Safety before scale',
    text: 'Consent gate before any health data, doctor review before any treatment decision, licensed pharmacy fulfilment only. We hold the line even when it slows growth.',
  },
  {
    icon: Stethoscope,
    title: 'Doctors at the centre',
    text: `Consultations, prescriptions and treatment decisions are made by independent licensed healthcare providers. ${BRAND_NAME} coordinates the care; it does not practise medicine.`,
  },
  {
    icon: Brain,
    title: 'AI-assisted, not AI-prescribed',
    text: 'AI supports intake, education, reminders and progress tracking. It does not diagnose, prescribe or override a doctor — and we say so on every screen.',
  },
  {
    icon: Compass,
    title: 'Built for Malaysia, made for SEA',
    text: 'Designed for Klang Valley first, with the rest of Malaysia and Southeast Asia in the roadmap. PDPA-conscious, locally relevant, doctor-led.',
  },
];

function ValueCard({ v, i }) {
  const r = useReveal({ threshold: 0.1 });
  const Icon = v.icon;
  return (
    <div
      ref={r.ref}
      className={`${r.className} card-soft p-7 h-full`}
      style={{ transitionDelay: `${i * 60}ms` }}
    >
      <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center">
        <Icon size={20} />
      </div>
      <h3 className="mt-5 font-bold text-ink-900 text-lg">{v.title}</h3>
      <p className="mt-2 text-sm text-ink-600 leading-relaxed">{v.text}</p>
    </div>
  );
}

export default function About() {
  return (
    <>
      <PageHero
        eyebrow={`About ${BRAND_NAME}`}
        title="Malaysia's AI-powered medical weight loss platform."
        subtitle={`${BRAND_NAME} is a healthcare technology and care coordination platform operated by Melonco Sdn. Bhd. We connect eligible adults with licensed healthcare providers and licensed pharmacy partners — through an AI-assisted assessment and ongoing digital support.`}
      />

      <section className="bg-white">
        <div className="container-x py-20 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <Reveal className="lg:col-span-5">
              <span className="eyebrow">Our mission</span>
              <h2 className="h-section mt-4">
                A safer route to medical weight loss.
              </h2>
              <p className="lead mt-5">
                Too many weight-loss journeys in Malaysia fall into two buckets:
                unverified slimming products with no oversight, or fragmented
                clinic visits with no clear follow-up. {BRAND_NAME} is building
                a third option — doctor-led, AI-assisted, calm.
              </p>
              <p className="mt-4 text-ink-600">
                Eligible adults complete an AI-assisted health assessment, are
                reviewed by a licensed healthcare provider, and — if suitable —
                receive a personalised plan. Where medication is prescribed,
                fulfilment is handled by licensed pharmacy partners. Throughout,
                AI helps you stay informed, consistent and supported.
              </p>
            </Reveal>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
              {VALUES.map((v, i) => <ValueCard key={v.title} v={v} i={i} />)}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-canvas border-y border-ink-200">
        <div className="container-x py-20 sm:py-24">
          <Reveal className="max-w-3xl">
            <span className="eyebrow">Roadmap</span>
            <h2 className="h-section mt-4">Where we are, and where we're heading.</h2>
            <p className="lead mt-4">
              We start with one focused, high-intent problem — medical weight
              management — and grow into a broader preventive health platform
              for Malaysians as we earn the trust and the operating maturity.
            </p>
          </Reveal>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <Reveal className="card-soft p-7" delay={0}>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-700">Phase 1 · Live</div>
              <h3 className="mt-2 text-xl font-bold">Medical weight loss</h3>
              <p className="mt-2 text-sm text-ink-600 leading-relaxed">
                AI-assisted assessment, licensed doctor consultation, licensed
                pharmacy fulfilment where appropriate, and follow-up support.
              </p>
            </Reveal>
            <Reveal className="card-soft p-7" delay={80}>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-700">Phase 1.5 · Now</div>
              <h3 className="mt-2 text-xl font-bold">Referrals & community</h3>
              <p className="mt-2 text-sm text-ink-600 leading-relaxed">
                Bringing in friends and family who could benefit — with safeguards
                to make sure rewards follow real, valid care, not signups.
              </p>
            </Reveal>
            <Reveal className="card-soft p-7" delay={160}>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-700">Phase 2 · Vision</div>
              <h3 className="mt-2 text-xl font-bold">Preventive health</h3>
              <p className="mt-2 text-sm text-ink-600 leading-relaxed">
                Biomarker tracking, AI longevity insights, preventive coordination.
                Future direction only — see the{' '}
                <Link to="/preventive-health" className="text-brand-700 underline">
                  vision page
                </Link>.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-x py-20 sm:py-24 max-w-3xl">
          <Reveal>
            <span className="eyebrow">Important</span>
            <h2 className="h-section mt-4">What {BRAND_NAME} is — and what it isn't.</h2>
            <div className="mt-6 space-y-4 text-ink-700 leading-relaxed">
              <p>
                <strong className="text-ink-900">{BRAND_NAME} is a healthcare technology and care coordination platform.</strong>{' '}
                It does not itself practise medicine, diagnose medical conditions,
                prescribe medication or dispense medication.
              </p>
              <p>
                Medical consultations, diagnoses, prescriptions and treatment
                decisions are made solely by licensed healthcare providers
                exercising independent clinical judgement. Medication, where
                prescribed, is dispensed by licensed pharmacy partners according
                to Malaysian requirements.
              </p>
              <p>
                The AI-assisted assessment is an intake and support tool. It
                does not create a prescription, replace a doctor or provide
                emergency medical services. Results vary between individuals
                and cannot be guaranteed. For urgent symptoms, seek immediate
                medical attention.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-brand">
                Talk to us <ArrowRight size={16} />
              </Link>
              <Link to="/how-it-works" className="btn-outline">See how it works</Link>
              <WhatsAppCta sourcePage="about" variant="ghost" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
